import { createSelector } from 'reselect';
import { SimpleUserGroupDto } from '../../../common/PricePrgClient';
import { RootState } from '../../../store';
import { notNull } from '../../../utils';
import { getAvailableResources, getCurrentUserPermissions, getUsers } from '../../user/redux/userSelectors';
import { UserGroupPagePermission, UserWithGroups } from '../userTypes';

export const getUserGroups = (state: RootState) => state.userGroup.userGroups.groups;
export const getUserGroupPermissions = (state: RootState) => state.userGroup.userGroups.permissions;
export const getSelectedUserGroupId = (state: RootState) => state.userGroup.ui.selectedUserGroupId;

export const getSelectedUserGroup = createSelector(
    [getSelectedUserGroupId, getUserGroups],
    (selectedUserGroupId, userGroups) => {
        if (selectedUserGroupId == null) {
            return null;
        }

        return userGroups[selectedUserGroupId];
    }
);

export const getAvailableUserGroups = createSelector(
    [getCurrentUserPermissions, getUserGroups],
    (permissions, userGroups) => {
        if (permissions.isAdmin) {
            return Object.values(userGroups).map(
                c => ({ name: c.name, userGroupId: c.userGroupId } as SimpleUserGroupDto)
            );
        } else {
            return permissions.userGroups;
        }
    }
);

export const getUserGroupMembers = createSelector(
    [getSelectedUserGroupId, getUserGroups, getUsers],
    (selectedUserGroupId, userGroups, users) => {
        if (selectedUserGroupId == null) {
            return [];
        }

        const userGroup = userGroups[selectedUserGroupId];
        if (userGroup == null) {
            return [];
        }

        return userGroup.members
            .map(memberId => users[memberId])
            .filter(notNull)
            .sort((a, b) => a.fullName.toLocaleLowerCase().localeCompare(b.fullName.toLocaleLowerCase()));
    }
);

export const getUsersWithGroups = createSelector([getUsers, getUserGroups], (users, groups) => {
    const usersWithGroups: UserWithGroups[] = [];

    const userArr = Object.values(users);
    const groupsArr = Object.values(groups).sort((a, b) =>
        a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase())
    );

    for (const user of userArr) {
        const userWithGroups: UserWithGroups = {
            ...user,
            groups: []
        };

        for (const group of groupsArr) {
            if (group.members.includes(user.userId)) {
                userWithGroups.groups.push(group);
            }
        }

        usersWithGroups.push(userWithGroups);
    }

    return usersWithGroups;
});

export const getMissingMembersFromGroup = createSelector(
    [getSelectedUserGroupId, getUserGroups, getUsers],
    (selectedUserGroupId, userGroups, users) => {
        if (selectedUserGroupId == null) {
            return [];
        }

        const userGroup = userGroups[selectedUserGroupId];
        if (userGroup == null) {
            return [];
        }

        return Object.values(users).filter(user => !userGroup.members.includes(user.userId));
    }
);

export const getUserGroupPagePermissions = createSelector(
    [getSelectedUserGroup, getUserGroupPermissions, getAvailableResources],
    (selectedUserGroup, userGroupPermissions, availablePermissions) => {
        if (selectedUserGroup == null) {
            return [];
        }

        const userGroupHasAccessArr = selectedUserGroup.permissions
            .map(userGroupPermissionId => userGroupPermissions[userGroupPermissionId])
            .filter(notNull)
            .filter(perm => perm.canRead)
            .map(perm => perm.resource);

        return Object.values(availablePermissions.pageResources).map(
            vp =>
                ({
                    ...vp,
                    hasAccess: userGroupHasAccessArr.includes(vp.resourceType)
                } as UserGroupPagePermission)
        );
    }
);
